import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { CustomUserClaims, UserClaims } from '@okta/okta-auth-js';
import { AuthFacadeService } from 'ngpd-merceros-authentication-fe-components';
import { BehaviorSubject, Observable, Subscription, from, interval } from 'rxjs';

import environment from '../../environments/environment';
import { LoggedUser } from '../interfaces/user.interface';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    public loggedUser: BehaviorSubject<LoggedUser> = new BehaviorSubject<LoggedUser>(null);

    public isPendoInitiated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    timer = interval(2000); // 2 secs timer for pendo script check

    attempt = 0;

    maxAttempts = 5;

    subscription: Subscription;

    private readonly api: string = environment.apiBaseUrl;

    showTips = signal<boolean>(null);

    /**
     *
     * @param http
     * @param _authFacade
     * @param router
     * @param oktaAuth
     * @param ngZone
     */
    constructor(
        private http: HttpClient,
        private _authFacade: AuthFacadeService,
        private router: Router,
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
        private ngZone: NgZone
    ) {
        this.retreiveTokenData();
    }

    /**
     *
     */
    private get userInfo(): LoggedUser {
        return this.loggedUser.getValue();
    }

    /**
     *
     */
    private get userTokenData(): any {
        return localStorage.getItem('auth_token')
            ? this.parseJwt(localStorage.getItem('auth_token'))
            : null;
    }

    /**
     *
     */
    getUserAsObservable() {
        return this.loggedUser.asObservable();
    }

    /**
     *
     */
    getIsPendoInitiated() {
        return this.isPendoInitiated.asObservable();
    }

    /**
     *
     */
    isAuthenticatedUser() {
        return !!this.userInfo;
    }

    /**
     *
     */
    async retreiveTokenData() {
        const tokenPayload = this.userTokenData;
        if (tokenPayload) {
            this.setUserData(tokenPayload);
            this.initiateTrackData();
        }
    }

    /**
     *
     * @param data
     */
    setUserData(data: LoggedUser): void {
        this.loggedUser.next(data);
    }

    /**
     *
     */
    getLastLoginAt() {
        const tipsSeen = localStorage.getItem('aida_tips_shown');
        if (tipsSeen === 'true') {
            return;
        }
        const user = this.getUserData();
        if (!user?.lastLogin) {
            this.showTips.set(false);
            return;
        }
        this.showTips.set(true);
    }

    /**
     *
     */
    getUserData(): LoggedUser | null {
        const userInfo = this.userInfo || null;
        this.loggedUser.next(userInfo);
        return userInfo;
    }

    /**
     * Returns true when the provided user (or current user) has Aida Admin access.
     * Uses token payload `access_roles` (or nested `relayState.access_roles`) to
     * determine admin status. Mirrors server-side logic in TAAP controller.
     * @param user
     */
    isAidaAdmin(user?: LoggedUser): boolean {
        // If caller passed a user object that already contains access_roles, honor it
        const accessRolesFromUser =
            (user as any)?.access_roles || (user as any)?.issuerTokenPayload?.access_roles;
        let hasAidaAdmin = false;
        if (typeof accessRolesFromUser === 'string') {
            hasAidaAdmin = accessRolesFromUser.toLowerCase().includes('aida admin');
        } else if (Array.isArray(accessRolesFromUser)) {
            hasAidaAdmin = accessRolesFromUser
                .map((r: any) => String(r).toLowerCase())
                .includes('aida admin');
        } else {
            // Otherwise parse the stored token payload (mirrors TAAP controller logic)
            const payload = this.userTokenData;
            const accessRoles = this.getAccessRolesFromPayload(payload);
            hasAidaAdmin =
                typeof accessRoles === 'string' && accessRoles.toLowerCase().includes('aida admin');
        }

        const username = (user as any)?.username || this.getUserData()?.username;
        const usernameLower = username ? String(username).toLowerCase() : undefined;

        return hasAidaAdmin || environment.ADMIN_DASHBOARD_USERS.includes(usernameLower);
    }

    /**
     * Extract `access_roles` from a token payload object or nested `relayState`.
     * @param payload
     */
    private getAccessRolesFromPayload(payload: Record<string, any> | null): string | undefined {
        if (!payload || typeof payload !== 'object') {
            return undefined;
        }

        if (typeof payload.access_roles === 'string') {
            return payload.access_roles;
        }

        if (typeof payload.relayState !== 'string') {
            return undefined;
        }

        try {
            const relayPayload = this.parseJwt(payload.relayState);
            if (typeof relayPayload?.access_roles === 'string') {
                return relayPayload.access_roles;
            }
        } catch {
            // ignore parsing errors
        }

        return undefined;
    }

    /** Pendo Works: Start */
    /**
     *
     */
    initiateTrackData(): void {
        this.ngZone.runOutsideAngular(() => {
            const userData = this.getUserData();

            if (environment.PENDO_APP_KEY_AIDA && environment.PENDO_ENABLE && userData) {
                const domain = userData.email?.replace(/.*@/, '');
                const visitorId = userData.globalprofileid?.toString();
                const pendo = (window as any)?.pendo;

                if (typeof pendo?.initialize === 'function') {
                    pendo.initialize({
                        visitor: {
                            id: visitorId,
                            domain,
                        },
                        account: {
                            id: this.getAccountId(),
                            domain,
                        },
                    });
                    this.isPendoInitiated.next(true);
                } else {
                    console.warn('Pendo script not loaded');
                }
            }
        });
    }

    /**
     *
     */
    getAccountId(): string {
        const url = window.location.href;
        const arr = url.split('/');
        const host = arr.length >= 3 ? arr[2] : '';
        const applicationAccountName = 'aida';
        let applicationEnvironment = 'prod';

        if (host.toLowerCase().indexOf('localhost') > -1) {
            applicationEnvironment = 'local';
        } else if (host.toLowerCase().indexOf('.dev.') > -1) {
            applicationEnvironment = 'dev';
        } else if (
            host.toLowerCase().indexOf('.stg.') > -1 ||
            host.toLowerCase().indexOf('uat') > -1
        ) {
            applicationEnvironment = 'stage';
        } else if (host.toLowerCase().indexOf('.prd.') > -1) {
            applicationEnvironment = 'prod';
        }

        return `${applicationAccountName}-${applicationEnvironment}`;
    }
    /** Pendo Works: End */

    /**
     *
     */
    ngOnDestroy(): void {
        // Clearing the interval if someone left open observable before script loaded check method
        this.subscription?.unsubscribe();
    }

    /**
     *
     */
    getUserRole() {
        // TODO: call api to get user role
        const mock_user = 'luis.ruidias@mmc.com';
        const role = this.userInfo.email === mock_user ? 'super-admin' : '';
        return role;
    }

    /**
     *
     * @param token
     */
    private parseJwt(token: string): any {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );
        return JSON.parse(jsonPayload);
    }

    /**
     * changes to use post method instead of get method
     * @param email
     */
    getHomeRealm(email: string): any {
        return this.http.post(`${environment.apiBaseUrl}/msso/homerealm`, { username: email });
    }

    /**
     *
     */
    clearStorage() {
        const navOptions = localStorage.getItem('navOptions');
        localStorage.clear();

        if (navOptions !== null) {
            localStorage.setItem('navOptions', navOptions);
        }

        sessionStorage.clear();
    }

    /**
     *
     * @param redirectUrl
     */
    clearAndRedirect(redirectUrl: string) {
        this.clearStorage();
        sessionStorage.setItem('post_logout_redirect', redirectUrl);
    }

    /**
     * Returns an Observable with the user credentials if authenticated.
     * @returns {Observable<UserClaims<CustomUserClaims>>} An observable containing user credentials or undefined.
     */
    getUserCredentials(): Observable<UserClaims<CustomUserClaims>> {
        const authState = this.oktaAuth.authStateManager.getAuthState();
        if (authState && authState.isAuthenticated) {
            return from(this.oktaAuth.getUser());
        }
        return from([undefined]);
    }

    /**
     * Signs the user out using Okta authentication.
     */
    signOut() {
        this.oktaAuth.signOut();
    }

    /**
     *
     */
    logOut() {
        // Reset BehaviorSubjects
        this.loggedUser.next(null);
    }
}
